import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card } from './ui/card';
import { Play, Pause } from 'lucide-react';

export const ServiceVideoCard = ({ video, title, description, testid }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
      setIsPlaying(true);
    }
  };

  const handlePause = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  useEffect(() => {
    if (isHovering) {
      handlePlay();
    } else {
      handlePause();
    }
  }, [isHovering]);

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.99 }}
      transition={{ type: 'spring', stiffness: 220, damping: 20 }}
      data-testid={testid}
    >
      <Card className="group relative overflow-hidden rounded-xl border hover:shadow-lg transition-shadow duration-300">
        <div
          className="aspect-video w-full overflow-hidden relative"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          role="img"
          aria-label={title}
        >
          <video
            ref={videoRef}
            className="w-full h-full object-cover transition-[filter] duration-300 group-hover:brightness-110"
            muted
            playsInline
            loop
            preload="metadata"
          >
            <source src={video} type="video/mp4" />
          </video>
          
          {/* Play Icon - Always visible when not playing */}
          {!isPlaying && (
            <div className="absolute inset-0 flex items-center justify-center transition-opacity duration-300 pointer-events-none">
              <div className="bg-black/60 backdrop-blur-sm rounded-full p-5 group-hover:scale-110 transition-transform">
                <Play className="h-10 w-10 text-white fill-white" />
              </div>
            </div>
          )}
          
          {/* Play/Pause Indicator on Hover */}
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
            <div className="bg-black/50 rounded-full p-4">
              {isPlaying ? (
                <Pause className="h-8 w-8 text-white" />
              ) : (
                <Play className="h-8 w-8 text-white" />
              )}
            </div>
          </div>
        </div>
        
        <div className="absolute inset-x-0 bottom-0 p-6 text-white bg-gradient-to-t from-black/80 via-black/50 to-transparent pointer-events-none">
          <h3 className="text-xl font-semibold mb-2" data-testid={`${testid}-title`}>{title}</h3>
          <p className="text-sm text-white/90" data-testid={`${testid}-description`}>{description}</p>
        </div>
      </Card>
    </motion.div>
  );
};
